import React from "react";
import { UserCoverStyle, UserResponse, ArticlePreviewResponse, ProfileUserResponse } from "@authory-packages/core";
import { EmbedMode, EmbedTheme } from "../../utils/embeds";
import { getDisplayName } from "../../utils/getDisplayName";
import { V3ProfileAvatar } from "../ProfileAvatar";
import { AuthoryColorSchemes } from "../ProfileHero/V3ProfileHero.types";
import { V3ProfileContact, V3ProfileSocials } from "../ProfileHero/V3ProfileSocials";
import { V3EmbedCollectionSeparator, V3EmbedCollectionTitle, V3EmbedCTA } from "../V3EmbedCollection";
import { V3EmbedContentListItem } from "../V3EmbedCollection/V3EmbedContentListitem";
import { EmbedWrapper } from "../V3EmbedContent";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    V3EmbedProfileAvatarWrapper,
    V3EmbedProfileBio,
    V3EmbedProfileContent,
    V3EmbedProfileFooter,
    V3EmbedProfileItemsImageWrapper,
    V3EmbedProfileNoItemsImageWrapper
} from "./V3EmbedProfile.styles";
import { UnstyledLink } from "../ReusableStyledComponents/UnstyledLink";
import { ProfileSocialsContainer } from "../ProfileHero/V4ProfileHero.styles";
import { decodeEntities } from "../../utils/decodeHTMLEntities";
import { useTheme } from "styled-components";
import { getAuthoryColorScheme } from "../ProfileHero/V4ProfileHero.utils";
import { CustomDarkShade } from "../ReusableStyledComponents/FontSetUtilities";
import { ItemUrlContext } from "../V3ProfileList/V3ProfileList.utils";

interface V3EmbedProfileProps {
    embedTheme?: EmbedTheme;
    embedMode?: EmbedMode;
    author: UserResponse | ProfileUserResponse;
    items: ArticlePreviewResponse[];
    showSocialMedia?: boolean;
    showHighlight?: boolean;
    useCover?: boolean;
    numBioLines?: number;
}

export const V3EmbedProfile = ({
    embedTheme = EmbedTheme.light,
    embedMode = EmbedMode.normal,
    author,
    items,
    showSocialMedia = true,
    showHighlight = true,
    useCover = true,
    numBioLines = 1
}: V3EmbedProfileProps) => {
    const theme = useTheme();
    const src = !!author.avatarUrl.length ? author.avatarUrl[0].url : undefined;
    const target = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${author.slug}`;
    const colorVariant = getAuthoryColorScheme(author.coverStyle as UserCoverStyle);
    const hasItems = !!items.length;
    const hasBackgroundImage = author.coverUrl.length > 1 && author.coverStyle !== UserCoverStyle.none && useCover;

    const useCustomItemsWrapper = hasBackgroundImage && hasItems;
    const ItemsWrapperToUse = useCustomItemsWrapper ? V3EmbedProfileItemsImageWrapper : React.Fragment;
    const ItemsWrapperToUseProps = useCustomItemsWrapper ? { $embedTheme: embedTheme } : {};
    const FooterToUse = hasBackgroundImage && !hasItems ? V3EmbedProfileFooter : React.Fragment;

    const useSeparator = (hasItems && !hasBackgroundImage) || !hasBackgroundImage;

    // If user has a cover, that takes precedence
    // otherwise we leverage the custom background color feature to enforce white/dark background for embeds, which are the only ones supported
    const customBackgroundColor = hasBackgroundImage ? undefined : embedTheme === EmbedTheme.light ? "#FFFFFF" : "#000000";

    const Content = () => (
        <>
            <V3EmbedProfileContent $embedMode={embedMode} $imgSrc={hasItems && hasBackgroundImage ? author.coverUrl[1].url : undefined}>
                {!!src && (
                    <VerticalSpacing bottom={embedMode === EmbedMode.normal ? 8 : 0} style={{ display: "inline-flex" }}>
                        <UnstyledLink href={target} target="_blank">
                            <V3EmbedProfileAvatarWrapper>
                                <V3ProfileAvatar
                                    size={embedMode === EmbedMode.normal ? "EMBED_NORMAL" : "EMBED_COMPACT"}
                                    src={src}
                                    authorName={getDisplayName(author)}
                                />
                            </V3EmbedProfileAvatarWrapper>
                        </UnstyledLink>
                    </VerticalSpacing>
                )}
                <div>
                    <VerticalSpacing bottom={10}>
                        <UnstyledLink href={target} target="_blank">
                            <V3EmbedCollectionTitle
                                as="h2"
                                $embedTheme={hasBackgroundImage ? EmbedTheme.dark : embedTheme}
                                $numLines={1}
                                $skipBaseColor
                                $customBackgroundColor={customBackgroundColor}
                            >
                                {getDisplayName(author)}
                            </V3EmbedCollectionTitle>
                        </UnstyledLink>
                    </VerticalSpacing>
                    {!!author.bio.length && (
                        <VerticalSpacing bottom={10}>
                            <UnstyledLink href={target} target="_blank">
                                <V3EmbedProfileBio
                                    $embedTheme={hasBackgroundImage ? EmbedTheme.dark : embedTheme}
                                    $numLines={numBioLines}
                                    $skipBaseColor
                                    $colorVariant={colorVariant === AuthoryColorSchemes.light ? colorVariant : undefined}
                                    $customBackgroundColor={customBackgroundColor}
                                    $customDarkShade={CustomDarkShade.Grey48}
                                >
                                    {decodeEntities(author.bio.split("\n")[0])}
                                </V3EmbedProfileBio>
                            </UnstyledLink>
                        </VerticalSpacing>
                    )}
                    <ProfileSocialsContainer $isEmbed $EmbedMode={embedMode} $editMode={false} $profileStyle={theme.customization.profileStyle}>
                        <div>
                            <V3ProfileSocials
                                colorVariant={colorVariant === AuthoryColorSchemes.light ? colorVariant : undefined}
                                author={author}
                                customJustify={embedMode === EmbedMode.compact ? "flex-start" : undefined}
                                showSocialMedia={showSocialMedia}
                                profileIconLimit={4}
                                customBackgroundColor={customBackgroundColor}
                            />
                        </div>
                        <div>
                            <V3ProfileContact
                                customBackgroundColor={customBackgroundColor}
                                openContactModal={() => {
                                    window.open(`${target}?target=contact`, "_blank");
                                }}
                                showHighlight={showHighlight}
                                colorVariant={colorVariant === AuthoryColorSchemes.light ? colorVariant : undefined}
                                author={author}
                                isEmbed
                            />
                        </div>
                    </ProfileSocialsContainer>
                </div>
            </V3EmbedProfileContent>
            {useSeparator && (
                <VerticalSpacing bottom={20} top={16}>
                    <V3EmbedCollectionSeparator />
                </VerticalSpacing>
            )}
            <ItemsWrapperToUse {...ItemsWrapperToUseProps}>
                {items.map((item, index) => (
                    <VerticalSpacing bottom={index === items.length - 1 ? 20 : 16} key={index}>
                        <V3EmbedContentListItem item={item} embedTheme={embedTheme} authorSlug={author.slug} context={ItemUrlContext.profile} />
                    </VerticalSpacing>
                ))}
                <FooterToUse>
                    <V3EmbedCTA target={target} label="View portfolio" embedTheme={embedTheme} utmCampaign="portfolio" />
                </FooterToUse>
            </ItemsWrapperToUse>
        </>
    );

    if (hasBackgroundImage) {
        return (
            <V3EmbedProfileNoItemsImageWrapper $imgSrc={!hasItems ? author.coverUrl[1].url : undefined}>
                <Content />
            </V3EmbedProfileNoItemsImageWrapper>
        );
    }

    return (
        <EmbedWrapper embedTheme={embedTheme}>
            <Content />
        </EmbedWrapper>
    );
};
